import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Button } from 'antd';

import { DataTableView, Column, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { ObjectUtils } from 'src/core/utils/object';
import { ToastNotifications } from 'src/core/ui/toast';
import { FormatService } from 'src/core/services/format.service';
import { InvoiceEntryListDataService } from './invoice-entry-list.data';
import { BreakdownList } from 'src/app/accounting/suppliers/cost/parts/breakdown-list';
import { SummaryTableHeader } from 'src/app/accounting/shared/summary-table-header';

interface Props extends ComponentViewProps<InvoiceSummary> {
}

@observer
export class InvoiceEntryList extends React.Component<Props, {}> {
	@resolve
	private formatService: FormatService;

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: DataSet<InvoiceEntrySummary>;

	componentDidMount(): void {
		const { setBusy } = this.props;

		setBusy(true);
		this.loadData()
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				setBusy(false);
			});
	}

	private async loadData(): Promise<void> {
		const { model } = this.props;
		this.dataService = InvoiceEntryListDataService.make();

		this.dataService.addFilter('invoiceId', 'eq', model.id);
		this.dataService.addSorting('id', 'asc', true);
		this.dataService.pageSize = 50;

		await this.dataService.load(query => query);
	}

	private getInvoicedSummary = (): string => {
		const { model } = this.props;
		const items = this.dataService.items.slice();

		const sum = items
			.reduce((prev, curr) => prev + curr.price, 0);

		return this.formatService.toString('currency', ObjectUtils.round(sum, 2), model.currencyFormat);
	};

	private renderItemsPerLine(items: any[]): React.ReactNode {
		return items.map((m, index) => (
			<React.Fragment key={index}>
				{index > 0 && <br />}
				{m}
			</React.Fragment>
		));
	}

	private handleExpandedRowRender = (entry: InvoiceEntrySummary): React.ReactNode => (
		<BreakdownList
			quoteEntryId={entry.quoteEntryId}
			currencyFormat={this.props.model.currencyFormat}
			setBusy={this.props.setBusy}
		/>
	);

	render(): React.ReactNode {
		if (this.dataService == null || this.dataService.items == null) {
			return null;
		}

		const { model, setBusy } = this.props;

		const columns: Column[] = [];
		columns.push(
			{
				title: 'Supplier',
				key: 'supplier',
				dataIndex: 'supplierTitle',
				width: 150
			},
			{
				title: 'Product',
				key: 'product',
				dataIndex: 'productDisplayName',
				width: 200,
			},
			{
				title: 'Booking Status',
				key: 'bookingStatus',
				dataIndex: 'bookingStatus',
				width: 150
			},
			{
				title: 'Clients',
				key: 'clientDisplayNames',
				dataIndex: 'clientsDisplayNames',
				width: 100,
				render: (field: string) => this.renderItemsPerLine(field.split(','))
			},
			{
				title: 'Client/Unit',
				key: 'occupancyText',
				dataIndex: 'occupancyText',
				width: 100
			},
			{
				title: 'Confirmation Reference',
				key: 'confirmationReferenceNumber',
				dataIndex: 'confirmationReferenceNumber',
				width: 100
			},
			{
				title: 'Booking Reference',
				key: 'bookingReference',
				dataIndex: 'bookingReference',
				width: 100
			},
			{
				title: 'Reservation Date',
				key: 'reservationDate',
				dataIndex: 'reservationDate',
				width: 150,
				render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
			},
			{
				key: 'dates',
				title: 'Service Date',
				children: [
					{
						key: 'dateFrom',
						dataIndex: 'dateFrom',
						title: 'From',
						width: 150,
						render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
					},
					{
						key: 'dateTo',
						dataIndex: 'dateTo',
						title: 'To',
						width: 150,
						render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
					}
				]
			},
			{
				title: 'Invoiced',
				key: 'totalGross',
				dataIndex: 'totalGross',
				width: 100,
				render: (field: any, record: InvoiceEntrySummary) => this.formatService.toString('currency', field, model.currencyFormat)
			}
		);

		return (
			<React.Fragment>
				<SummaryTableHeader
					fields={[
						{ label: 'Invoiced', value: this.getInvoicedSummary() }
					]}
				/>
			<DataTableView
				mode="display"
				rowKey={(record) => record.id.toString()}
				model={this.dataService}
				columns={columns}
				setBusy={setBusy}
				expandedRowRender={this.handleExpandedRowRender}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
				selection={false}
				otherProps={{
					scroll: { x: 1635, y: 400 }
				}}
			/>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/shared/invoices/list/parts/invoice-entry-list.tsx